import {
  CalendarClock,
  CheckCircle2,
  FileText,
  Lock,
  type LucideIcon,
  PenLine,
  Receipt,
  ShieldCheck,
  SlidersHorizontal,
  TrendingUp,
  Wallet,
} from "lucide-react";

import type { TimelineEvent, TimelineKind } from "@/lib/contractTimeline";

const ICON: Record<TimelineKind, LucideIcon> = {
  created: FileText,
  signed: PenLine,
  activated: CheckCircle2,
  ended: CalendarClock,
  closed: Lock,
  invoice: Receipt,
  indexation: TrendingUp,
  cost_adjustment: SlidersHorizontal,
  deposit_received: ShieldCheck,
  deposit_settled: Wallet,
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** A quiet vertical timeline. Pass `limit` for the Overview preview. */
export function ContractTimeline({
  events,
  limit,
}: {
  events: TimelineEvent[];
  limit?: number;
}) {
  const shown = limit ? events.slice(0, limit) : events;
  if (shown.length === 0)
    return <p className="text-sm text-muted-foreground">Nothing has happened yet.</p>;

  return (
    <ol className="space-y-0">
      {shown.map((e, i) => {
        const Icon = ICON[e.kind];
        const last = i === shown.length - 1;
        return (
          <li key={`${e.kind}-${e.date}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground">
                <Icon className="size-3.5" />
              </span>
              {!last && <span className="my-1 w-px flex-1 bg-border" />}
            </div>
            <div className={last ? "min-w-0" : "min-w-0 pb-5"}>
              <p className="text-sm font-medium leading-tight">{e.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {fmtDate(e.date)}
                {e.detail ? ` · ${e.detail}` : ""}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
